import React, { useState } from 'react';
import { Eraser, Loader2, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CHECKER = { backgroundImage: 'repeating-conic-gradient(rgba(0,0,0,0.12) 0 25%, transparent 0 50%)', backgroundSize: '16px 16px' };

// Knocks the background out of an image to true transparency (server-side), with preview.
export const BackgroundRemover = ({ src, imgKey, onApplied }) => {
  const { removeImageBackground } = useAuth();
  const [tolerance, setTolerance] = useState(30);
  const [preview, setPreview] = useState(null); // { preview, removed_pct }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const run = async (save) => {
    setBusy(true); setErr('');
    try {
      const r = await removeImageBackground(src, tolerance, save);
      if (save) { await onApplied(r.url); setPreview(null); }
      else setPreview(r);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not process the image.'); }
    setBusy(false);
  };

  if (!src) return null;
  return (
    <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(122,86,44,0.07)', border: '1px solid rgba(122,86,44,0.25)' }} data-testid={`bg-remove-${imgKey}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Eraser size={15} style={{ color: '#7a562c' }} />
        <span className="font-title text-sm" style={{ color: '#7a562c' }}>Remove background (make transparent)</span>
      </div>
      <p className="text-xs mb-2" style={{ color: 'var(--olive)' }}>Knocks out the plain background around the graphic so the page shows through. Raise the tolerance if some background remains; lower it if parts of the graphic disappear.</p>
      <label className="flex items-center gap-2 text-xs mb-2" style={{ color: 'var(--navy)' }}>
        Tolerance
        <input type="range" min={0} max={100} value={tolerance} onChange={(e) => { setTolerance(Number(e.target.value)); setPreview(null); }} className="flex-1" data-testid={`bg-remove-tolerance-${imgKey}`} />
        <span className="w-8 text-right">{tolerance}</span>
      </label>
      {preview && (
        <div className="grid grid-cols-2 gap-2 mb-2" data-testid={`bg-remove-preview-${imgKey}`}>
          <div className="rounded overflow-hidden" style={CHECKER}><img src={src} alt="before" className="w-full h-24 object-contain" /><div className="text-[10px] text-center py-0.5 bg-white/80">Before</div></div>
          <div className="rounded overflow-hidden" style={CHECKER}><img src={preview.preview} alt="after" className="w-full h-24 object-contain" data-testid={`bg-remove-after-${imgKey}`} /><div className="text-[10px] text-center py-0.5 bg-white/80">After · {preview.removed_pct}% removed</div></div>
        </div>
      )}
      {err && <p className="text-xs mb-2" style={{ color: 'var(--maroon)' }} data-testid={`bg-remove-error-${imgKey}`}>{err}</p>}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => run(false)} disabled={busy} data-testid={`bg-remove-preview-btn-${imgKey}`} className="px-3 py-2 rounded text-sm font-title bg-white inline-flex items-center gap-1" style={{ border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)', opacity: busy ? 0.6 : 1 }}>
          {busy && !preview ? <Loader2 size={14} className="animate-spin" /> : <Eraser size={14} />} Preview
        </button>
        {preview && (
          <button type="button" onClick={() => run(true)} disabled={busy} data-testid={`bg-remove-apply-${imgKey}`} className="px-3 py-2 rounded text-sm font-title inline-flex items-center gap-1" style={{ background: '#7a562c', color: 'var(--cream)', opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Use transparent version
          </button>
        )}
      </div>
    </div>
  );
};
